
import { defineComponent, type PropType } from 'vue'

import { RunStatus } from '~/types/types'
import { formatSeconds, fromNow } from '~/utils/date'
import { RouteNavigationParams } from '~/utils/router'

import { type GeneralizedRunT } from '~/utils/runs'
import { shortenLargeNumber } from '~/utils/string'
import { runStatusIcon, runStatusIconColor } from '~/utils/ui'

type IssueStatsT = {
  label: string
  value: number | string
  type: 'juniper' | 'cherry' | 'amber'
}

export default defineComponent({
  name: 'RunUICard',
  props: {
    title: {
      type: String as PropType<GeneralizedRunT['title']>,
      default: ''
    },
    status: {
      type: String as PropType<GeneralizedRunT['status']>,
      default: RunStatus.Pass
    },
    branchName: {
      type: String as PropType<GeneralizedRunT['branchName']>,
      default: ''
    },
    createdAt: {
      type: String as PropType<GeneralizedRunT['createdAt']>,
      default: ''
    },
    runId: {
      type: String as PropType<GeneralizedRunT['runId']>,
      default: ''
    },
    commitOid: {
      type: String as PropType<GeneralizedRunT['commitOid']>,
      default: ''
    },
    prNumber: {
      type: String as PropType<GeneralizedRunT['prNumber']>,
      default: ''
    },
    issuesRaisedCount: {
      type: Number as PropType<GeneralizedRunT['issuesRaisedCount']>,
      default: 0
    },
    issuesResolvedCount: {
      type: Number as PropType<GeneralizedRunT['issuesResolvedCount']>,
      default: 0
    },
    issuesSuppressedCount: {
      type: Number as PropType<GeneralizedRunT['issuesSuppressedCount']>,
      default: 0
    },
    finishedIn: {
      type: Number as PropType<GeneralizedRunT['finishedIn']>,
      default: ''
    },
    isSecondary: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    icon(): string {
      return runStatusIcon(this.status)
    },
    iconColor(): string {
      return runStatusIconColor(this.status)
    },
    isPending(): boolean {
      return this.status === RunStatus.Pend
    },
    showIssueStats(): boolean {
      return (
        !this.isPending &&
        Boolean(this.issuesRaisedCount || this.issuesResolvedCount || this.issuesSuppressedCount)
      )
    },
    issueStats(): IssueStatsT[] {
      return [
        {
          label: 'fixed',
          value: this.issuesResolvedCount ? shortenLargeNumber(this.issuesResolvedCount) : 0,
          type: 'juniper'
        },
        {
          label: 'introduced',
          value: this.issuesRaisedCount ? shortenLargeNumber(this.issuesRaisedCount) : 0,
          type: 'cherry'
        },
        {
          label: 'suppressed',
          value: this.issuesSuppressedCount ? shortenLargeNumber(this.issuesSuppressedCount) : 0,
          type: 'amber'
        }
      ]
    },
    createdString(): string | null {
      return this.createdAt
        ? this.finishedIn
          ? `${formatSeconds(this.finishedIn, true)} (${fromNow(this.createdAt)})`
          : `${fromNow(this.createdAt)}`
        : null
    },
    runLink(): RouteNavigationParams {
      return {
        name: 'provider-owner-repo-run-runId',
        params: {
          ...this.$route.params,
          runId: this.runId
        }
      }
    }
  }
})
